'use client';

import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

interface FacebookPixelState {
  pixelId: string | null;
  isReady: boolean;
}

const initialState: FacebookPixelState = {
  pixelId: null,
  isReady: false,
};

let cachedState: FacebookPixelState = initialState;
let initPromise: Promise<void> | null = null;
const subscribers = new Set<(s: FacebookPixelState) => void>();

function publish(next: FacebookPixelState) {
  cachedState = next;
  subscribers.forEach((cb) => cb(next));
}

function ensureInited() {
  if (initPromise) return initPromise;

  const baseUrl = getApiBaseUrl();

  initPromise = fetch(`${baseUrl}/api/public/config`)
    .then((res) => res.json())
    .then((data: { metaPixelId: string | null }) => {
      const pixelId = data.metaPixelId;
      if (!pixelId) {
        publish({ pixelId: null, isReady: true });
        return;
      }

      // Standard Meta Pixel bootstrap stub — queues calls until fbevents.js loads.
      if (typeof (window as any).fbq === 'undefined') {
        const n: any = ((window as any).fbq = function (...args: any[]) {
          if (n.callMethod) {
            n.callMethod(...args);
          } else {
            n.queue.push(args);
          }
        });
        if (!(window as any)._fbq) (window as any)._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
      }

      publish({ pixelId, isReady: true });

      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.async = true;
      document.head.appendChild(script);

      (window as any).fbq('init', pixelId);
      (window as any).fbq('track', 'PageView');
    })
    .catch(() => {
      publish({ pixelId: null, isReady: true });
    });

  return initPromise;
}

export function useFacebookPixel(): FacebookPixelState {
  const [state, setState] = useState<FacebookPixelState>(cachedState);

  useEffect(() => {
    subscribers.add(setState);
    setState(cachedState);
    // Defer off the critical boot path — the pixel loads a third-party script
    // (fbevents.js) and isn't needed for first paint; running it during idle
    // keeps it from competing with hydration on the main thread.
    const hasRIC = typeof window !== 'undefined' && 'requestIdleCallback' in window;
    const id = hasRIC
      ? window.requestIdleCallback(() => { void ensureInited(); }, { timeout: 3000 })
      : window.setTimeout(() => { void ensureInited(); }, 1500);
    return () => {
      subscribers.delete(setState);
      if (hasRIC) window.cancelIdleCallback(id as number);
      else window.clearTimeout(id as number);
    };
  }, []);

  return state;
}
